import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Heart, Bookmark, Share2, Flag, Clock, Volume2, Pause, Square } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { ArticleCard } from "../components/ArticleCard";
import { onImgError } from "../lib/imageFallback";
import { SponsoredCard } from "../components/SponsoredCard";
import { AffiliateCard } from "../components/AffiliateCard";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { ReportDialog } from "../components/ReportDialog";
import { JsonLd, usePageMeta } from "../components/JsonLd";
import { SignupPopup } from "../components/SignupPopup";

const SITE = "https://picturethisink.com";

const MD = {
  img: ({ node, ...props }) => <img {...props} alt={props.alt || ""} onError={onImgError} className="w-full my-6 rounded" />,
  a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" className="text-pti-red underline" />,
  video: ({ node, ...props }) => <video {...props} controls playsInline className="w-full my-6 rounded bg-black" />,
  hr: () => <hr className="my-9 border-0 border-t-2 border-pti-red" />,
  h2: ({ node, ...props }) => <h2 {...props} className="font-display text-2xl sm:text-3xl font-bold text-pti-black mt-10 mb-3" />,
  h3: ({ node, ...props }) => <h3 {...props} className="font-display text-xl font-bold text-pti-black mt-8 mb-2" />,
};

export default function ArticleDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const { t } = useT();
  const [a, setA] = useState(null);
  const [ads, setAds] = useState([]);
  const [affInline, setAffInline] = useState(null);
  const [affSidebar, setAffSidebar] = useState(null);
  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);
  const [following, setFollowing] = useState(false);
  const [comment, setComment] = useState("");
  const [reportOpen, setReportOpen] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/articles/${slug}`).then((r) => setA(r.data)).catch(() => setA(false));
    api.get("/ads/active?placement=article").then((r) => setAds(r.data)).catch(() => {});
  }, [slug]);

  // Stop any read-aloud when leaving the article or switching articles.
  useEffect(() => {
    return () => { if (window.speechSynthesis) window.speechSynthesis.cancel(); };
  }, [slug]);

  useEffect(() => {
    if (!a || !a.category) return;
    const c = encodeURIComponent(a.category);
    api.get(`/affiliate?placement=article_inline&category=${c}`).then((r) => setAffInline(r.data[0] || null)).catch(() => {});
    api.get(`/affiliate?placement=article_sidebar&category=${c}`).then((r) => setAffSidebar(r.data[0] || null)).catch(() => {});
  }, [a?.category]); // eslint-disable-line

  const metaDesc = a ? (a.excerpt || a.title) : "";
  usePageMeta({ title: a ? `${a.title} — PictureThisInk` : "PictureThisInk", description: metaDesc });

  if (a === false) return <div className="pti-container py-24 text-center">Article not found.</div>;
  if (!a) return <div className="pti-container py-24 text-center text-gray-400">Loading…</div>;

  const canonical = `${SITE}/article/${a.slug}`;
  const articleLd = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: a.title,
    description: a.excerpt || a.title,
    image: a.cover_image ? [a.cover_image] : undefined,
    datePublished: a.published_date || a.created_at,
    dateModified: a.updated_at || a.published_date || a.created_at,
    articleSection: a.category,
    keywords: Array.isArray(a.tags) ? a.tags.join(", ") : undefined,
    author: { "@type": "Person", name: a.author?.display_name || a.author?.username || a.author_username || "PictureThisInk Editorial" },
    publisher: { "@type": "Organization", name: "PictureThisInk", logo: { "@type": "ImageObject", url: `${SITE}/logo.png` } },
    mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
    url: canonical,
  };

  const paras = (a.body || "").split("\n\n");
  const isHtml = /<(p|div|h[1-6]|img|video|ul|ol|li|figure|iframe|blockquote|strong|em|br)\b/i.test(a.body || "");
  const like = async () => { setLiked(true); await api.post(`/articles/${a.id}/like`).catch(() => {}); toast.success("Liked!"); };
  const save = () => { setSaved(!saved); toast.success(saved ? "Removed from saved" : "Saved for later"); };
  const share = async () => {
    try { await navigator.clipboard.writeText(window.location.href); toast.success("Link copied!"); } catch { toast.error("Could not copy"); }
  };
  const follow = async () => {
    if (!user) return toast.error("Sign in to follow creators.");
    const { data } = await api.post(`/creators/${a.author.username}/follow`);
    setFollowing(data.following);
  };
  const submitComment = async (e) => {
    e.preventDefault();
    if (!user) return toast.error("Sign in to comment.");
    try { const { data } = await api.post(`/content/${a.id}/comments`, { body: comment }); toast.success(data.message); setComment(""); }
    catch (err) { toast.error(apiErr(err)); }
  };

  const readAloud = () => {
    const synth = window.speechSynthesis;
    if (!synth) { toast.error("Read-aloud isn't supported in this browser."); return; }
    synth.cancel();
    const text = `${a.title}. ${(a.body || "").replace(/\s+/g, " ")}`;
    const u = new SpeechSynthesisUtterance(text);
    u.lang = "en-US";
    u.rate = 1;
    u.onend = () => { setSpeaking(false); setPaused(false); };
    u.onerror = () => { setSpeaking(false); setPaused(false); };
    synth.speak(u);
    setSpeaking(true); setPaused(false);
  };
  const togglePause = () => {
    const synth = window.speechSynthesis;
    if (paused) { synth.resume(); setPaused(false); } else { synth.pause(); setPaused(true); }
  };
  const stopReading = () => { window.speechSynthesis.cancel(); setSpeaking(false); setPaused(false); };

  return (
    <div className="pti-container py-10 grid lg:grid-cols-12 gap-10" data-testid="article-detail">
      <JsonLd id="article-jsonld" data={articleLd} />
      <SignupPopup />
      <article className="lg:col-span-8">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-bold">
          <span className="text-pti-red">{a.category}</span>
          {a.sponsored && <span className="bg-pti-black text-white px-2 py-0.5">SPONSORED</span>}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-black leading-tight mt-3" data-testid="article-title">{a.title}</h1>

        {a.sponsored && (
          <div className="mt-4 bg-gray-50 border-l-4 border-pti-red px-4 py-3 text-sm text-gray-700" data-testid="sponsored-disclosure">
            <strong>Sponsored content.</strong> This article was paid for by {a.author?.display_name || "a sponsor"}. Links below are marked as sponsored.
          </div>
        )}

        {/* Byline card */}
        <div className="flex flex-wrap items-center justify-between gap-4 mt-6 border-y border-gray-200 py-4">
          <Link to={`/creators/${a.author.username}`} className="flex items-center gap-3" data-testid="byline-card">
            <img src={a.author.avatar || "https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=200"} alt={a.author.display_name} className="w-12 h-12 rounded-full object-cover bg-gray-100" />
            <div>
              <div className="font-bold flex items-center gap-1">{a.author.display_name}{a.author.verified && <CheckCircle2 size={14} className="text-pti-red" aria-label="Verified creator" />}</div>
              <div className="text-sm text-gray-500 flex items-center gap-2">
                @{a.author.username}
                <span>·</span>
                <span>{new Date(a.published_date).toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" })}</span>
                <span>·</span>
                <span className="flex items-center gap-1"><Clock size={12} /> {a.read_time} min read</span>
              </div>
            </div>
          </Link>
          <button onClick={follow} data-testid="follow-btn" className={`px-4 py-2 text-sm font-bold border transition-colors ${following ? "bg-white text-pti-black border-pti-black" : "bg-pti-red text-white border-pti-red hover:bg-pti-redhover"}`}>
            {following ? "Following" : "Follow"}
          </button>
        </div>

        <p className="mt-3 text-xs text-gray-400" data-testid="article-ownership">
          Published by PictureThisInk · Powered by <a href="https://www.mishertech.com" target="_blank" rel="noopener noreferrer" aria-label="Visit MisherTech" className="font-semibold text-gray-500 hover:text-pti-red transition-colors">MisherTech</a>
        </p>

        <img src={a.cover_image} alt={a.cover_alt || a.title} onError={onImgError} className="w-full aspect-video object-cover mt-6 bg-gray-100" />

        {/* Read-aloud accessibility control */}
        <div className="mt-4 flex items-center gap-2" data-testid="read-aloud">
          {!speaking ? (
            <button onClick={readAloud} data-testid="read-aloud-start"
              className="flex items-center gap-2 bg-pti-black text-white px-4 py-2 text-sm font-bold hover:bg-pti-red transition-colors">
              <Volume2 size={16} /> {t("readAloud")}
            </button>
          ) : (
            <>
              <button onClick={togglePause} data-testid="read-aloud-pause"
                className="flex items-center gap-2 bg-pti-black text-white px-4 py-2 text-sm font-bold hover:bg-pti-red transition-colors">
                {paused ? <><Volume2 size={16} /> {t("resume")}</> : <><Pause size={16} /> {t("pause")}</>}
              </button>
              <button onClick={stopReading} data-testid="read-aloud-stop"
                className="flex items-center gap-2 border border-pti-black px-4 py-2 text-sm font-bold hover:bg-pti-black hover:text-white transition-colors">
                <Square size={14} /> {t("stop")}
              </button>
              <span className="flex items-center gap-1.5 text-xs font-bold text-pti-red" aria-live="polite">
                <span className="w-1.5 h-1.5 rounded-full bg-pti-red animate-pulse" aria-hidden="true" /> {paused ? t("pause") : "●"}
              </span>
            </>
          )}
        </div>

        {/* Body with native sponsor after 3rd paragraph */}
        <div className="prose prose-lg max-w-none mt-8 leading-relaxed text-gray-800">
          {isHtml ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={MD}>{a.body}</ReactMarkdown>
          ) : (
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={MD}>{paras.slice(0, 3).join("\n\n")}</ReactMarkdown>
          )}
          <div className="my-8" data-testid="article-inline-ad">
            {affInline
              ? <AffiliateCard ad={affInline} placement="article_inline" category={a.category} bypassOnePerPage />
              : ads[0] ? <SponsoredCard ad={ads[0]} label="Advertisement" /> : null}
          </div>
          {!isHtml && paras.length > 3 && <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={MD}>{paras.slice(3).join("\n\n")}</ReactMarkdown>}
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mt-8">
          {(a.tags || []).map((t) => <span key={t} className="text-xs font-semibold border border-gray-300 px-2 py-1">#{t}</span>)}
        </div>

        {/* Publishing links (compliant, rel=sponsored) */}
        {(a.publishing_links || []).length > 0 && (
          <div className="mt-8 border-t border-gray-200 pt-6" data-testid="publishing-links">
            <h3 className="text-xs font-bold uppercase tracking-widest text-gray-500">Links</h3>
            <ul className="mt-3 space-y-1.5">
              {a.publishing_links.map((l, i) => (
                <li key={i}>
                  <a href={l.url} target="_blank" rel="sponsored nofollow noopener noreferrer" className="text-pti-red underline break-words" data-testid={`publishing-link-${i}`}>
                    {l.label || l.url}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-4 mt-8 border-y border-gray-200 py-4">
          <button onClick={like} data-testid="article-like" className={`flex items-center gap-2 text-sm font-semibold ${liked ? "text-pti-red" : "text-gray-600 hover:text-pti-red"}`}><Heart size={18} fill={liked ? "#ef1831" : "none"} /> {a.likes + (liked ? 1 : 0)}</button>
          <button onClick={save} data-testid="article-save" className={`flex items-center gap-2 text-sm font-semibold ${saved ? "text-pti-red" : "text-gray-600 hover:text-pti-red"}`}><Bookmark size={18} fill={saved ? "#ef1831" : "none"} /> Save</button>
          <button onClick={share} data-testid="article-share" className="flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-pti-red"><Share2 size={18} /> Share</button>
          <button onClick={() => setReportOpen(true)} data-testid="article-report" className="flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-pti-red ml-auto"><Flag size={18} /> Report</button>
        </div>

        {/* Comments */}
        <section className="mt-10">
          <h2 className="font-display text-2xl font-bold">Comments</h2>
          <form onSubmit={submitComment} className="mt-4">
            <textarea value={comment} onChange={(e) => setComment(e.target.value)} required rows={3} placeholder={user ? "Add a comment (reviewed before publishing)…" : "Sign in to comment"}
              disabled={!user} data-testid="comment-input" className="w-full border border-gray-300 p-3 focus:border-pti-red outline-none" aria-label="Comment" />
            <button type="submit" disabled={!user} data-testid="comment-submit" className="mt-2 bg-pti-black text-white px-5 py-2 font-bold hover:bg-pti-red transition-colors disabled:opacity-40">Post comment</button>
          </form>
          <div className="mt-6 space-y-4">
            {(a.comments || []).length === 0 && <p className="text-gray-400 text-sm">No approved comments yet. Be the first.</p>}
            {(a.comments || []).map((c) => (
              <div key={c.id} className="flex gap-3 border-b border-gray-100 pb-4">
                <img src={c.author_avatar || "https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=100"} alt="" className="w-8 h-8 rounded-full bg-gray-100 object-cover" />
                <div><div className="text-sm font-bold">{c.author_name}</div><p className="text-sm text-gray-700">{c.body}</p></div>
              </div>
            ))}
          </div>
        </section>

        {/* Related */}
        {a.related?.length > 0 && (
          <section className="mt-12">
            <h2 className="font-display text-2xl font-bold border-b-2 border-pti-black pb-2">Related Articles</h2>
            <div className="grid sm:grid-cols-3 gap-6 mt-6">{a.related.map((r) => <ArticleCard key={r.id} article={r} />)}</div>
          </section>
        )}
      </article>

      {/* Sidebar */}
      <aside className="lg:col-span-4 space-y-6">
        <div className="lg:sticky lg:top-28 space-y-6">
          {affSidebar ? (
            <div className="hidden lg:block"><AffiliateCard ad={affSidebar} placement="article_sidebar" category={a.category} /></div>
          ) : (ads[1] || ads[0]) ? <SponsoredCard ad={ads[1] || ads[0]} label="Sponsored" /> : null}
          <div className="border border-gray-200 p-5">
            <h3 className="font-display text-lg font-bold">Write for PictureThisInk</h3>
            <p className="text-sm text-gray-600 mt-1">Join our creators and publish your own stories.</p>
            <Link to="/register" className="inline-block mt-3 bg-pti-red text-white px-4 py-2 text-sm font-bold hover:bg-pti-redhover transition-colors">Become a creator</Link>
          </div>
        </div>
      </aside>

      <ReportDialog open={reportOpen} onOpenChange={setReportOpen} contentType="article" contentId={a.id} />
    </div>
  );
}
